/**
 * ============================================================================
 * useActiveDashboard — the active dashboard's identity, label, and structure.
 * ============================================================================
 *
 * One hook so every surface that needs the dashboard identity (the header
 * switcher, the browser tab title, the coming-soon guard) agrees on what
 * "KFE - WBR" is called. NOTE: page headings deliberately do NOT use `label`
 * — they name the view only; identity lives once in the switcher (design
 * decisions log 2026-08-21). Display name and
 * review type come from GetPermissions when available (server-owned naming),
 * falling back to the structure so nothing blanks while the profile loads
 * (spec multi-dashboard R2/R4, dashboard-rename-kfe-quality R2.2).
 */

import { useAppSelector } from 'src/app/store';
import {
  selectServedDashboards,
  useGetPermissionsQuery,
} from 'src/features/api/permissionsApi';
import {
  dashboardStructure,
  DashboardStructure,
  toDashboardSlug,
} from 'src/config/dashboards';
import { WBR_REVIEW_TYPE } from 'src/constants';

export interface ActiveDashboard {
  dashboardId: string;
  /** Display name ("KFE", "Quality") — server-owned when the profile is loaded. */
  name: string;
  /** Review type ("WBR") — supportedReviewTypes[0], falling back to WBR. */
  reviewType: string;
  /** The product label every header uses: "KFE - WBR", "Quality - WBR". */
  label: string;
  /** URL slug for this dashboard ("kfe-wbr") — the base of every in-app path. */
  slug: string;
  structure: DashboardStructure;
}

export function useActiveDashboard(): ActiveDashboard {
  const dashboardId = useAppSelector((s) => s.dashboard.activeDashboardId);
  const { data: permissions } = useGetPermissionsQuery();
  const served = selectServedDashboards(permissions).find(
    (d) => d.dashboardId === dashboardId,
  );
  const structure = dashboardStructure(dashboardId);
  // Server-owned name once the profile loads; the structure's fallback
  // beforehand (never a toUpperCase() guess — the KFE wire id is "fai").
  const name = served?.name ?? structure.displayName;
  const reviewType = served?.supportedReviewTypes[0] ?? WBR_REVIEW_TYPE;
  return {
    dashboardId,
    name,
    reviewType,
    label: `${name} - ${reviewType}`,
    slug: toDashboardSlug(dashboardId, reviewType),
    structure,
  };
}
